<template lang="pug">
div(class='timeline__container')
  steptime
  timeline(:timelines='timelines' :colors='colorUrls' :motions='motions' :beginTime='beginTime' :timezone='timezone')
  .timeline__controls
    el-button(@click='back')
      fa(:icon='angleLeft')
    el-date-picker(
      v-model='beginTimeForPicker'
      type="date"
      placeholder="Pick a day"
      :clearable='false'
      :picker-options="pickerOptions")
    el-button(@click='next')
      fa(:icon='angleRight')
    .timeline__history-filter
      label.timeline__history-filter-field
        span С
        el-date-picker(
          v-model='historyFromInput'
          type='datetime'
          format='dd-MM-yyyy HH:mm:ss'
          placeholder='Выберите дату и время'
          :picker-options='historyFromPickerOptions'
          @change='applyHistoryPeriod')
      label.timeline__history-filter-field
        span По
        el-date-picker(
          v-model='historyToInput'
          type='datetime'
          format='dd-MM-yyyy HH:mm:ss'
          placeholder='Выберите дату и время'
          :picker-options='historyToPickerOptions'
          @change='applyHistoryPeriod')
      el-button.timeline__history-filter-reset(@click='resetHistoryPeriod') Сбросить
    div.timeline__stream__container(v-for='(item, index) in filteredColorUrls' :key='item.fromTs')
      div(
        class='timeline__stream_square'
        v-bind:style=`{ 'background-color': item.color }`)
        div(
          class='timeline__stream_square'
          v-bind:style=`{ 'background-color': item.color }`)
      p.timeline__stream_time {{getDate(item.fromTs) + ' — ' +getDate(item.toTs)}}
      p.timeline__stream_text(id='text') {{item.name}}
      el-button.timeline__stream_copy(
        icon='el-icon-document'
        type='text'
        @click='copyUrlToClipBoard(item.stream)'
        )

</template>

<script>
import {FontAwesomeIcon as fa} from "@fortawesome/vue-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { mapActions, mapState, mapMutations } from "vuex";
import { subMonths } from "date-fns";
import StepTime from "./StepTime";
import TimeLine from "./Timeline";
import copyBuffer from "@/core/mixins/copyBuffer";

export default {
  name: "TimelineContainer",
  components: {
    steptime: StepTime,
    timeline: TimeLine,
    fa,
  },
  mixins: [copyBuffer],
  props: {
    id: {
      type: Number,
      default: null,
    },
    timezone: {
      type: Number,
      default: 7
    }
  },
  data() {
    const historyPeriod = this.getDefaultHistoryPeriod();

    return {
      timer: null,
      beginTime: new Date().setHours(0, 0, 0, 0) / 1000,
      historyFromInput: historyPeriod.from,
      historyToInput: historyPeriod.to,
      appliedHistoryFrom: historyPeriod.from,
      appliedHistoryTo: historyPeriod.to,
    };
  },
  computed: {
    midnight() {
      return new Date().setHours(0, 0, 0, 0) / 1000;
    },
    angleLeft() {
      return faAngleLeft;
    },
    angleRight() {
      return faAngleRight;
    },
    colorUrls () {
      return this.$store.state.timeline.colorUrls
    },
    filteredColorUrls() {
      const from = this.appliedHistoryFrom ? this.appliedHistoryFrom.getTime() / 1000 : null;
      const to = this.appliedHistoryTo.getTime() / 1000;

      return this.colorUrls.filter((item) =>
        item.fromTs <= to && (from === null || item.toTs >= from)
      );
    },
    historyFromPickerOptions() {
      const to = this.historyToInput ? new Date(this.historyToInput) : new Date();
      const lastAvailableDate = new Date(Math.min(to.getTime(), Date.now()));
      const lastAvailableDay = new Date(lastAvailableDate).setHours(23, 59, 59, 999);
      const from = this.historyFromInput ? new Date(this.historyFromInput) : null;
      const isLastAvailableDay =
        from &&
        from.getFullYear() === lastAvailableDate.getFullYear() &&
        from.getMonth() === lastAvailableDate.getMonth() &&
        from.getDate() === lastAvailableDate.getDate();
      const lastAvailableTime = [
        lastAvailableDate.getHours(),
        lastAvailableDate.getMinutes(),
        lastAvailableDate.getSeconds(),
      ]
        .map((value) => ("0" + value).slice(-2))
        .join(":");

      return {
        selectableRange: isLastAvailableDay ? `00:00:00 - ${lastAvailableTime}` : "00:00:00 - 23:59:59",
        disabledDate(time) {
          return time.getTime() > lastAvailableDay;
        },
      };
    },
    historyToPickerOptions() {
      const from = this.historyFromInput ? new Date(this.historyFromInput) : null;
      const firstAvailableDay = from ? new Date(from).setHours(0, 0, 0, 0) : null;
      const lastAvailableDay = new Date().setHours(23, 59, 59, 999);

      return {
        disabledDate(time) {
          return (
            (firstAvailableDay !== null && time.getTime() < firstAvailableDay) ||
            time.getTime() > lastAvailableDay
          );
        },
      };
    },
    beginTimeForPicker: {
      get() {
        return this.beginTime * 1000;
      },
      set(value) {
        this.beginTime = new Date(value).setHours(0, 0, 0, 0) / 1000;
      },
    },
    pickerOptions() {
      const self = this;

      return {
        disabledDate(time) {
          return time.getTime() > self.endDate * 1000 || time.getTime() < self.startDate * 1000;
        },
      };
    },
    ...mapState("timeline", ["timelines", "motions"]),
  },
  watch: {
    id(value) {
      clearInterval(this.timer);

      this.getTimeline(value);
      this.getMotions(value);
      this.timer = setInterval(() => {
        this.getTimeline(value);
        this.getMotions(value);
      }, 600000);
    },
  },
  mounted() {
    this.getTimeline(this.$props.id);
    this.getMotions(this.$props.id);
    this.getTimelineHistory(this.$props.id)
  },
  destroyed() {
    clearInterval(this.timer);
    this.setTimeline([]);
    this.setMotions([]);
    this.setTimelineHistory([]);
  },
  methods: {
    applyHistoryPeriod() {
      const now = new Date();
      const from = this.historyFromInput ? new Date(this.historyFromInput) : null;

      if (!this.historyToInput) {
        this.historyToInput = now;
      }

      const to = new Date(this.historyToInput);
      if (from && from.getTime() > to.getTime()) {
        return;
      }

      this.appliedHistoryFrom = from;
      this.appliedHistoryTo = to;
    },
    resetHistoryPeriod() {
      const historyPeriod = this.getDefaultHistoryPeriod();

      this.historyFromInput = historyPeriod.from;
      this.historyToInput = historyPeriod.to;
      this.appliedHistoryFrom = historyPeriod.from;
      this.appliedHistoryTo = historyPeriod.to;
      this.getTimelineHistory(this.$props.id);
    },
    getDefaultHistoryPeriod() {
      const to = new Date();

      return {
        from: subMonths(to, 1),
        to,
      };
    },
    getColorStream(name) {
      let color = "#1B5E20";
      this.colorUrls.map((item) => {
        if (item.name === name) {
          color = `hsl(${item.color.h}, ${item.color.s}%, ${item.color.l}%)`;
        }
      });
      return color;
    },

    back() {
      const prevTime = this.beginTime - 3600 * 24;

      this.beginTime = prevTime;
    },
    next() {
      const nextTime = this.beginTime + 3600 * 24;

      this.beginTime = nextTime;
    },
    openSuccessMessage(message) {
      this.$message({
        message: message,
        type: "success",
      });
    },
    copyUrlToClipBoard(string) {
      this.copyData(string);
      this.openSuccessMessage("Скопировано");
    },
    getDate(timestamp) {
      let date = new Date();
      date.setTime(timestamp * 1000);
      const dateOut =
        date.getFullYear() + "-" + ("0" + (date.getMonth() + 1)).slice(-2) + "-" + ("0" + date.getDate()).slice(-2);
      const timeOut = ("0" + date.getHours()).slice(-2) + ":" + ("0" + date.getMinutes()).slice(-2);
      return `${dateOut} ${timeOut}`;
    },
    ...mapActions("timeline", {
      getTimeline: "GET_TIMELINE",
      getMotions: "GET_MOTIONS",
      getTimelineHistory: 'GET_TIMELINE_HISTORY'
    }),
    ...mapMutations("timeline", {
      setTimeline: "SET_TIMELINE",
      setMotions: "SET_MOTIONS",
      setTimelineHistory: 'SET_TIMELINE_HISTORY'
    }),
  },
};
</script>

<style lang="postcss">
.timeline__controls {
  text-align: center;
  padding: 15px 0;
}

.timeline__controls > * {
  margin-right: 10px;
}

.timeline__history-filter {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 15px 0;
}

.timeline__history-filter-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.timeline__history-filter-reset {
  width: 220px;
}

.timeline__stream_square {
  width: 15px;
  height: 15px;
  margin-top: auto;
  margin-bottom: auto;
}

.timeline__stream_time {
  margin-left: 10px;
}

.timeline__stream_text {
  margin-left: 30px;
}

.timeline__stream_copy {
  margin-left: 10px;
}

.timeline__stream__container {
  display: flex;
  flex-direction: row;
}
</style>
